import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { useTenant } from '../../../../providers/TenantProvider';
import { AdminCard } from '../components/AdminCard';
import { AdminToggle } from '../components/AdminToggle';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { adminRequest } from '../../../shared/services/api';

// Tab Plataforma — PRD-002 prompt 0568
// Solo SUPERADMIN_PLATFORM puede mutar (banner + form deshabilitado para resto).
// Reorganizado: 2 pills (Plan & Limites + Sub-apps M12). Pill "Modules" duplicada
// eliminada — ya existe Tab Modulos dedicado en /admin/modulos (0567).

type PillKey = 'plan' | 'apps';

interface AppM12 {
  codigo: string;
  nombre: string;
  activo: boolean;
}

interface PlanForm {
  plan: string;
  maxUsuarios: number;
  dominioPersonalizado: string;
}

const PLANES = ['BASIC', 'PROFESSIONAL', 'ENTERPRISE', 'PREMIUM'] as const;

const PILL_ACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: '#1F3864',
  color: '#fff',
  fontFamily: 'inherit',
};

const PILL_INACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#fff',
  color: '#6b7280',
  fontFamily: 'inherit',
};

const READONLY_BANNER: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  fontSize: 13,
  backgroundColor: '#EFF6FF',
  color: '#1E40AF',
  border: '1px solid #BFDBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 16,
};

export function AdminPlataforma() {
  const { t } = useI18n();
  const { isPlatformAdmin } = useAuth();
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? '';

  const [activePill, setActivePill] = useState<PillKey>('plan');
  const [apps, setApps] = useState<AppM12[]>([]);
  const [planForm, setPlanForm] = useState<PlanForm>({
    plan: 'BASIC',
    maxUsuarios: 10,
    dominioPersonalizado: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'warning' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, tenantRes] = await Promise.all([
        adminRequest<{ data: AppM12[] }>('/tenant/apps'),
        adminRequest<{
          data: { plan: string; maxUsuarios: number; dominioPersonalizado: string | null };
        }>('/tenant'),
      ]);
      setApps(appsRes.data ?? []);
      setPlanForm({
        plan: tenantRes.data?.plan ?? 'BASIC',
        maxUsuarios: tenantRes.data?.maxUsuarios ?? 10,
        dominioPersonalizado: tenantRes.data?.dominioPersonalizado ?? '',
      });
    } catch {
      // Fallback: catálogo APP1-APP7 inactivo
      setApps([
        { codigo: 'APP1', nombre: 'V·IA Compras', activo: false },
        { codigo: 'APP2', nombre: 'V·IA Personas', activo: false },
        { codigo: 'APP3', nombre: 'V·IA Proyectos', activo: false },
        { codigo: 'APP4', nombre: 'V·IA Sociedades', activo: false },
        { codigo: 'APP5', nombre: 'V·IA Centros', activo: false },
        { codigo: 'APP6', nombre: 'V·IA Solicita', activo: false },
        { codigo: 'APP7', nombre: 'V·IA Inventario', activo: false },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const toggleApp = async (codigo: string) => {
    if (!isPlatformAdmin) {
      showToast(t.admin.modulos.soloPlatformPuedeActivar, 'warning');
      return;
    }
    if (!tenantId) return;
    const app = apps.find((a) => a.codigo === codigo);
    if (!app) return;
    const newActive = !app.activo;

    setApps((prev) => prev.map((a) => (a.codigo === codigo ? { ...a, activo: newActive } : a)));

    try {
      await adminRequest(`/tenants/${tenantId}/apps/${codigo}`, {
        method: 'PUT',
        body: JSON.stringify({ active: newActive }),
      });
      showToast(`${codigo} ${newActive ? t.admin.modulos.activado : t.admin.modulos.desactivado}`);
    } catch (err) {
      setApps((prev) => prev.map((a) => (a.codigo === codigo ? { ...a, activo: !newActive } : a)));
      showToast(err instanceof Error ? err.message : t.admin.plataforma.appUpdateErr, 'warning');
    }
  };

  const savePlan = async () => {
    if (!isPlatformAdmin) {
      showToast(t.admin.modulos.soloPlatformPuedeActivar, 'warning');
      return;
    }
    if (!tenantId) return;
    setSaving(true);
    try {
      await adminRequest(`/tenants/${tenantId}/platform-config`, {
        method: 'PUT',
        body: JSON.stringify({
          plan: planForm.plan,
          maxUsuarios: planForm.maxUsuarios,
          dominioPersonalizado: planForm.dominioPersonalizado || null,
        }),
      });
      showToast(t.admin.plataforma.planSaveOk);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.admin.plataforma.planSaveErr, 'warning');
    } finally {
      setSaving(false);
    }
  };

  const pills: { key: PillKey; label: string }[] = [
    { key: 'plan', label: t.admin.plataforma.pills.plan },
    { key: 'apps', label: t.admin.plataforma.pills.apps },
  ];

  return (
    <div style={{ display: 'flex', gap: 24, padding: 24 }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 50,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
            color: '#fff',
            backgroundColor: toast.type === 'warning' ? '#F59E0B' : '#059669',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Left pills */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, flexShrink: 0 }}
      >
        {pills.map((p) => (
          <button
            key={p.key}
            onClick={() => setActivePill(p.key)}
            style={activePill === p.key ? PILL_ACTIVE : PILL_INACTIVE}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Right content */}
      <div style={{ flex: 1 }}>
        {!isPlatformAdmin && (
          <div style={READONLY_BANNER}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>{t.admin.modulos.soloPlatformPuedeActivar}</span>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <LoadingSpinner size="sm" />
          </div>
        ) : activePill === 'plan' ? (
          <AdminCard title={t.admin.plataforma.planCardTitle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480 }}>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  {t.admin.plataforma.planLabel}
                </label>
                <select
                  value={planForm.plan}
                  onChange={(e) => setPlanForm((p) => ({ ...p, plan: e.target.value }))}
                  disabled={!isPlatformAdmin}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    fontFamily: 'inherit',
                    backgroundColor: isPlatformAdmin ? '#fff' : '#F3F4F6',
                    color: '#111827',
                  }}
                >
                  {PLANES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  {t.admin.plataforma.maxUsuariosLabel}
                </label>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={planForm.maxUsuarios}
                  onChange={(e) =>
                    setPlanForm((p) => ({ ...p, maxUsuarios: parseInt(e.target.value) || 0 }))
                  }
                  disabled={!isPlatformAdmin}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    fontFamily: 'inherit',
                    backgroundColor: isPlatformAdmin ? '#fff' : '#F3F4F6',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  {t.admin.plataforma.dominioLabel}
                </label>
                <input
                  type="text"
                  placeholder="ej. portal.miempresa.com"
                  value={planForm.dominioPersonalizado}
                  onChange={(e) =>
                    setPlanForm((p) => ({ ...p, dominioPersonalizado: e.target.value }))
                  }
                  disabled={!isPlatformAdmin}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    fontFamily: 'inherit',
                    backgroundColor: isPlatformAdmin ? '#fff' : '#F3F4F6',
                  }}
                />
                <p style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  {t.admin.plataforma.dominioHint}
                </p>
              </div>

              {isPlatformAdmin && (
                <button
                  onClick={savePlan}
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: 'none',
                    backgroundColor: '#1F3864',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: saving ? 0.5 : 1,
                    alignSelf: 'flex-start',
                  }}
                >
                  {saving ? t.admin.plataforma.planSaving : t.admin.plataforma.planSaveBtn}
                </button>
              )}
            </div>
          </AdminCard>
        ) : (
          <AdminCard title={t.admin.plataforma.appsTitle}>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
              {t.admin.plataforma.appsDesc}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {apps.map((app) => (
                <div
                  key={app.codigo}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    backgroundColor: app.activo ? '#fff' : '#fafafa',
                    opacity: app.activo ? 1 : 0.6,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1F3864' }}>
                      {app.nombre}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{app.codigo}</div>
                  </div>
                  <AdminToggle
                    checked={app.activo}
                    disabled={!isPlatformAdmin}
                    onChange={() => toggleApp(app.codigo)}
                  />
                </div>
              ))}
            </div>
          </AdminCard>
        )}
      </div>
    </div>
  );
}
